import { useEffect, useRef, useState, type FC } from "react";
import { useDropdownAlignment } from "../../../hooks/useDropdownAlignment";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";
import styled from "styled-components";
import { theme } from "../../../theme";
import BodySmall from "../../atoms/typography/BodySmall";
import { CalendarIcon } from "../../atoms/icons/CalendarIcon";
import { ChevronSmallDownIcon } from "../../atoms/icons/ChevronIcons";
import { useTranslation } from "react-i18next";
import DatePicker from "./DatePicker";
import Divider from "../../atoms/Divider";
import { MediaQuery } from "../../../utils/design";
import DefaultButton from "../../atoms/buttons/DefaultButton";
import ActionButton from "../../atoms/buttons/ActionButton";
import { CheckmarkIconSmall } from "../../atoms/icons/CheckmarkIcons";
import dayjs from "dayjs";

interface Props {
  from: Date | undefined;
  to: Date | undefined;
  onSelect: (range: { from?: Date; to?: Date }) => void;
  titleKey?: string;
}

const DatePickerContainer = styled.div`
  border-bottom: 1.25px solid ${theme.colors.grey40};
  padding: 0px 8px;
  height: 36px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  background-color: transparent;

  &:hover {
    cursor: pointer;
    background-color: ${theme.colors.hover};
  }
`;

const RangeSelectionContainer = styled.div<{ $alignRight: boolean }>`
  position: absolute;
  top: calc(100% + 4px);
  z-index: 10;
  ${({ $alignRight }) => ($alignRight ? "right: 0;" : "left: 0;")}

  background-color: ${theme.colors.white};
  border-radius: 4px;
  border: 1px solid ${theme.colors.grey40};
  box-shadow: ${theme.shadow.cardShadow};
  padding: 1rem;

  display: flex;

  width: fit-content;
`;

const DatePickerWrapper = styled.div`
  font-family: "Inter", sans-serif;
  padding: 16px;
  color: ${theme.colors.text};
  font-size: 16px;
`;

const StyledDatePicker = styled(DayPicker)`
  --rdp-accent-color: ${theme.colors.primary};
  --rdp-background-color: #0f5156; /* Ensures full color */
  --rdp-accent-light: rgba(15, 81, 86, 0.2);
  --rdp-accent-background-color: ${theme.colors.primary60};

  .rdp-day_selected,
  .rdp-day_selected:hover {
    background-color: var(--rdp-accent-color) !important;
    color: white !important;
    border-radius: 5px;
  }

  .rdp-day_range_start,
  .rdp-day_range_end {
    background-color: var(--rdp-accent-color) !important;
    color: white !important;
  }

  ${MediaQuery.SM} {
    min-width: 650px;
  }
`;

const SelectTypesContainer = styled.div`
  width: 176px;
  padding: 16px 8px 0px 8px;
  border-right: 1px solid ${theme.colors.grey05};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SelectTypeButtonStyle = styled.button`
  display: flex;
  gap: 8px;
  align-items: center;
  border: none;
  border: none;
  background-color: transparent;
  padding: 8px;
  border-radius: 4px;

  &:hover {
    background-color: ${theme.colors.hover};
    cursor: pointer;
  }
`;

const SELECT_TYPES = [
  {
    titleKey: "dates.allDates",
    value: "allDates",
    range: { from: undefined, to: undefined },
  },
  {
    titleKey: "dates.lastWeek",
    value: "lastWeek",
    range: {
      from: dayjs().subtract(1, "week").startOf("week").toDate(),
      to: dayjs().subtract(1, "week").endOf("week").toDate(),
    },
  },
  {
    titleKey: "dates.lastSevenDays",
    value: "lastSevenDays",
    range: {
      from: dayjs().subtract(7, "day").toDate(),
      to: dayjs().toDate(),
    },
  },
  {
    titleKey: "dates.lastThirtyDays",
    value: "lastThirtyDays",
    range: {
      from: dayjs().subtract(30, "day").toDate(),
      to: dayjs().toDate(),
    },
  },
  {
    titleKey: "dates.thisMonth",
    value: "thisMonth",
    range: {
      from: dayjs().startOf("month").toDate(),
      to: dayjs().endOf("day").toDate(),
    },
  },
  {
    titleKey: "dates.thisYear",
    value: "thisYear",
    range: {
      from: dayjs().startOf("year").toDate(),
      to: dayjs().endOf("day").toDate(),
    },
  },
  {
    titleKey: "dates.lastYear",
    value: "lastYear",
    range: {
      from: dayjs().subtract(1, "year").startOf("year").toDate(),
      to: dayjs().subtract(1, "year").endOf("year").toDate(),
    },
  },
];

interface SelectTypeButtonProps {
  title: string;
  selected: boolean;
  onClick: () => void;
}
const SelectTypeButton: FC<SelectTypeButtonProps> = ({
  title,
  selected,
  onClick,
}) => {
  return (
    <SelectTypeButtonStyle onClick={onClick}>
      <BodySmall fontweight={selected ? 600 : 400}>{title}</BodySmall>
      {selected && <CheckmarkIconSmall />}
    </SelectTypeButtonStyle>
  );
};

const DateRangePicker: FC<Props> = ({ from, to, onSelect, titleKey }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const { panelRef, alignRight } = useDropdownAlignment(open);
  const [range, setRange] = useState<DateRange | undefined>({
    from: from,
    to: to,
  });
  const [typeRangeSelection, setTypeRangeSelection] = useState(
    range?.from === undefined && range?.to === undefined
      ? "allDates"
      : undefined,
  );

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  const handleSelect = (selected: DateRange | undefined) => {
    if (selected) {
      setRange(selected);
      setTypeRangeSelection(undefined);
    }
  };

  const onClickSelectTypeDates = (
    typeButtonValue: string,
    range: DateRange,
  ) => {
    setTypeRangeSelection(typeButtonValue);
    setRange(range);
  };

  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      <DatePickerContainer onClick={() => setOpen(!open)}>
        <CalendarIcon />
        <BodySmall fontweight={600}>{t(titleKey ?? "dates.date")}:</BodySmall>
        <BodySmall>
          {typeRangeSelection
            ? t(`dates.${typeRangeSelection}`)
            : `${dayjs(range?.from).format("YYYY-MM-DD")} - ${dayjs(
                range?.to,
              ).format("YYYY-MM-DD")}`}
        </BodySmall>
        <ChevronSmallDownIcon />
      </DatePickerContainer>

      {open && (
        <RangeSelectionContainer ref={panelRef} $alignRight={alignRight}>
          <SelectTypesContainer>
            {SELECT_TYPES.map((typeButton) => (
              <SelectTypeButton
                key={typeButton.value}
                title={t(typeButton.titleKey)}
                selected={typeRangeSelection === typeButton.value}
                onClick={() =>
                  onClickSelectTypeDates(typeButton.value, typeButton.range)
                }
              />
            ))}
          </SelectTypesContainer>
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                padding: 16,
              }}
            >
              <DatePicker
                value={range?.from}
                onChange={(date) => {
                  setRange({ from: date, to: range?.to });
                  setTypeRangeSelection(undefined);
                }}
              />
              <BodySmall>-</BodySmall>
              <DatePicker
                value={range?.to}
                onChange={(date) => {
                  setRange({ from: range?.from, to: date });
                  setTypeRangeSelection(undefined);
                }}
              />
            </div>
            <Divider />
            <div>
              <DatePickerWrapper>
                <StyledDatePicker
                  mode="range"
                  selected={range}
                  onSelect={handleSelect}
                  numberOfMonths={2}
                />
              </DatePickerWrapper>
            </div>
            <Divider />
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                padding: 16,
                gap: 32,
              }}
            >
              <DefaultButton
                title={t("common.cancel")}
                onClick={() => setOpen(false)}
              />
              <ActionButton
                title={t("common.select")}
                onClick={() => {
                  onSelect({ from: range?.from, to: range?.to });
                  setOpen(false);
                }}
                color={theme.colors.primary}
              />
            </div>
          </div>
        </RangeSelectionContainer>
      )}
    </div>
  );
};

export default DateRangePicker;
